import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import "moment/locale/fr";
import { useNavigate } from "react-router-dom";

moment.locale("fr");

const STATUS_LABELS = {
  pending: "En attente",
  completed: "Complété",
  refunded: "Remboursé",
  failed: "Échoué",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  refunded: "bg-blue-100 text-blue-800 border-blue-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);
  const navigate = useNavigate();

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const updatePaymentStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await updateDoc(doc(db, "payments", id), { status: newStatus });
      setPayments((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: newStatus } : p))
      );
      showNotif("Statut mis à jour !");
    } catch {
      alert("Erreur lors de la mise à jour");
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    async function fetchPayments() {
      setLoading(true);
      const snap = await getDocs(collection(db, "payments"));
      const data = [];

      for (const docSnap of snap.docs) {
        const payment = docSnap.data();

        let clientName = "";
        let couturierName = "";

        if (payment.clientId && typeof payment.clientId === "string") {
          const clientSnap = await getDoc(doc(db, "users", payment.clientId));
          if (clientSnap.exists()) {
            const clientData = clientSnap.data();
            clientName =
              clientData.displayName ||
              clientData.name ||
              clientData.email ||
              "";
          }
        }
        if (payment.couturierId && typeof payment.couturierId === "string") {
          const couturierSnap = await getDoc(
            doc(db, "users", payment.couturierId)
          );
          if (couturierSnap.exists()) {
            const couturierData = couturierSnap.data();
            couturierName =
              couturierData.displayName ||
              couturierData.name ||
              couturierData.email ||
              "";
          }
        }

        data.push({
          id: docSnap.id,
          appointmentId: payment.appointmentId,
          clientId: payment.clientId,
          couturierId: payment.couturierId,
          clientName,
          couturierName,
          amount: payment.amount || 0,
          tvaAmount: payment.tvaAmount || 0,
          commissionAmount: payment.commissionAmount || 0,
          totalAmount: payment.totalAmount || 0,
          status: payment.status,
          createdAt: payment.createdAt,
        });
      }

      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setPayments(data);
      setLoading(false);
    }
    fetchPayments();
  }, []);

  const filteredPayments =
    filterStatus === "all"
      ? payments
      : payments.filter((p) => p.status === filterStatus);

  const statusCounts = {
    all: payments.length,
    pending: payments.filter((p) => p.status === "pending").length,
    completed: payments.filter((p) => p.status === "completed").length,
    refunded: payments.filter((p) => p.status === "refunded").length,
    failed: payments.filter((p) => p.status === "failed").length,
  };

  const totalEncaisse = payments
    .filter((p) => p.status === "completed")
    .reduce((sum, p) => sum + (p.totalAmount || 0), 0);

  const totalCommissions = payments
    .filter((p) => p.status === "completed")
    .reduce((sum, p) => sum + (p.commissionAmount || 0), 0);

  const totalTva = payments
    .filter((p) => p.status === "completed")
    .reduce((sum, p) => sum + (p.tvaAmount || 0), 0);

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total encaissé</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{totalEncaisse.toFixed(2)} €</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Commissions</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{totalCommissions.toFixed(2)} €</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">TVA</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{totalTva.toFixed(2)} €</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Paiements</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{payments.length}</p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
            {[
              { key: "all", label: "Tous" },
              { key: "pending", label: "En attente" },
              { key: "completed", label: "Complétés" },
              { key: "refunded", label: "Remboursés" },
              { key: "failed", label: "Échoués" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filterStatus === key
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label} ({statusCounts[key]})
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            {filteredPayments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucun paiement trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Couturier</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Montant HT</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">TVA</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Total TTC</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredPayments.map((p) => (
                      <tr key={p.id} onClick={() => navigate(`/rdv/${p.appointmentId}`)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-3 py-2.5 text-gray-900">{p.clientName || <span className="text-xs italic text-gray-400">Inconnu</span>}</td>
                        <td className="px-3 py-2.5 text-gray-900">{p.couturierName || <span className="text-xs italic text-gray-400">Inconnu</span>}</td>
                        <td className="px-3 py-2.5 font-medium">{(p.amount || 0).toFixed(2)} €</td>
                        <td className="px-3 py-2.5 font-medium">{(p.commissionAmount || 0).toFixed(2)} €</td>
                        <td className="px-3 py-2.5 font-medium">{(p.tvaAmount || 0).toFixed(2)} €</td>
                        <td className="px-3 py-2.5 font-semibold text-gray-900">{(p.totalAmount || 0).toFixed(2)} €</td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            disabled={updatingId === p.id}
                            value={p.status}
                            onChange={(e) => updatePaymentStatus(p.id, e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] disabled:opacity-50"
                          >
                            {Object.entries(STATUS_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                          {p.createdAt && p.createdAt.toDate
                            ? moment(p.createdAt.toDate()).format("DD/MM/YYYY HH:mm")
                            : p.createdAt
                            ? moment(p.createdAt).format("DD/MM/YYYY HH:mm")
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
